import React, { useEffect, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { compact, each, filter, get, isArray, isEmpty, keys, omitBy, sortBy, uniq } from 'lodash';
import { useTranslation } from 'react-i18next';
import { Box } from '@mui/system';
import { Grid2, IconButton } from '@mui/material';
import { KeyboardArrowUp, KeyboardArrowDown } from '@mui/icons-material';

import { findPatients } from './_api';
import { IField, IPatientResponse, IPatientSearchForm } from './_types';

import useAlerts from 'components/Alerts/useAlerts';
import { Papeer } from 'components/Papeer/Papeer';
import FormInput from 'components/Form/Input/Input';
import FormSelect from 'components/Form/Select/Select';
import FormDatePicker from 'components/Form/Date/Date';

import { useAppGlobals } from 'utils/hooks/useAppGlobals';
import { useActions } from 'utils/hooks/useActions';
import { useLanguage } from 'utils/hooks/useLanguage';
import useRefetchGrid from 'utils/grid/useRefetchGrid';
import { useStudy } from 'utils/hooks/useStudy';

import { getClinicalPortalSetting } from 'modules/Administration/ClinicalPortal/_api';
import { useSearch } from 'utils/hooks/useSearch';
import { useStorage } from 'utils/hooks/useStorage';
import Button from 'components/Buttons/Button';
import { useAppInfo } from 'utils/hooks/useAppInfo';

const apiDateTimeFormat = "yyyy-MM-dd'T'HH:mm:ss";
const mainSx = { width: { xs: '100%', md: '50%', lg: '20%' } };

const FORM_DATA_KEY = 'patientSearchFormFhir';
const OTHER_DATA_KEY = 'patientSearchFormOtherFhir';

const PatientSearchForm: React.FC<IPatientSearchForm> = ({ setCanShowResults }) => {
  const refSubmitButtom = useRef<HTMLButtonElement>(null);
  const { t } = useTranslation('SearchForm');
  const { storeClinicalPortalSetting, storePatients, setLists } = useActions();
  const { clinicalPortalSettingFromRedux } = useAppInfo();
  const { getPatientSexSlug, linkBack } = useStudy();
  const { formatDateTimeForSearch } = useSearch();
  const { getItem, putItem, removeItem } = useStorage();

  const { addErrorAlert } = useAlerts();
  const { refetchGrid } = useRefetchGrid();

  const [fields, setFields] = useState<IField[]>([]);
  const [searchFieldOpened, setSearchFieldOpened] = useState(false);

  const { currentLocale } = useLanguage();

  const methods = useForm();

  const {
    handleSubmit,
    formState: { errors },
    watch,
    setFocus,
    setValue,
  } = methods;

  const formValues = watch();
  const patientBirthDateFrom = watch('patientBirthDateFrom');
  const patientBirthDateTo = watch('patientBirthDateTo');
  const canSearch =
    !isEmpty(omitBy(formValues, isEmpty)) || patientBirthDateFrom || patientBirthDateTo;
  const { toggleLoader } = useAppGlobals();

  const backFromDetail = linkBack();

  const submitHandler = async (formValues: any) => {
    toggleLoader();
    callPersistForm(formValues);
    const values = omitBy(formValues, isEmpty);
    const patientBirthDateFrom = get(formValues, 'patientBirthDateFrom')
      ? formatDateTimeForSearch(get(formValues, 'patientBirthDateFrom'), apiDateTimeFormat, 'start')
      : null;
    const patientBirthDateTo = get(formValues, 'patientBirthDateTo')
      ? formatDateTimeForSearch(get(formValues, 'patientBirthDateTo', ''), apiDateTimeFormat, 'end')
      : null;
    const resp = await findPatients({
      ...values,
      ...(patientBirthDateFrom ? { patientBirthDateFrom } : {}),
      ...(patientBirthDateTo ? { patientBirthDateTo } : {}),
    });
    storePatients([]);
    let allUnitSexes: string[] = [];
    if (isArray(resp)) {
      const patientResults: IPatientResponse[] = resp.map((item, index) => {
        const sex = get(item, 'sex', '');
        const sexTranslated = sex ? t(getPatientSexSlug(sex)) : '';
        allUnitSexes.push(sexTranslated);
        return {
          ...item,
          id: index + 1,
          internalID: get(item, 'masterPatientID', ''),
          sexTranslated,
        };
      });
      storePatients(patientResults);
      setLists({ uniqSexes: uniq(compact(allUnitSexes)) });
      setCanShowResults(true);
    } else {
      addErrorAlert(t('submitError'));
    }
    refetchGrid();

    toggleLoader(false);
  };

  const triggerSubmit = () => {
    refSubmitButtom?.current?.click();
  };

  const resetFields = () => {
    each(keys(formValues), (key: string) => {
      setValue(key, null);
    });
  };

  const loadEntities = async () => {
    toggleLoader();

    if (!backFromDetail) {
      storePatients(null);
    }

    let clinicalPortal = clinicalPortalSettingFromRedux;
    if (!clinicalPortal) {
      clinicalPortal = await getClinicalPortalSetting();
      storeClinicalPortalSetting(clinicalPortal);
    }
    if (clinicalPortal) {
      const searchAttributes = filter(get(clinicalPortal, 'searchAttributes', []), {
        active: true,
      });
      const sexes = [
        { id: '', label: t('all') },
        { id: 'F', label: t('female') },
        { id: 'M', label: t('male') },
        { id: 'O', label: t('other') },
      ];
      const fields: IField[] = searchAttributes.map((item) => {
        const fhirCode = get(item, 'fhirCode', null);
        let nameTranslate = get(item, 'nameTranslate', null);
        if (nameTranslate) {
          nameTranslate = JSON.parse(nameTranslate);
        }
        return {
          name: fhirCode,
          label: get(nameTranslate, currentLocale, fhirCode),
          position: get(item, 'position', 0),
          searchType: get(item, 'basic', false) ? 'basic' : 'extended',
          type:
            fhirCode === 'patientSex'
              ? 'select'
              : fhirCode === 'patientBirthDateFrom' || fhirCode === 'patientBirthDateTo'
              ? 'datePicker'
              : null,
          items: fhirCode === 'patientSex' ? sexes : null,
        };
      });
      setFields(sortBy(fields, ['position']));
    }

    if (backFromDetail) {
      const data = getItem(FORM_DATA_KEY);
      if (data) {
        // Parse it to a javaScript object
        try {
          const values = JSON.parse(data);
          const transformedValues = {
            ...values,
            patientBirthDateFrom: values.patientBirthDateFrom
              ? new Date(values.patientBirthDateFrom)
              : null,
            patientBirthDateTo: values.patientBirthDateTo
              ? new Date(values.patientBirthDateTo)
              : null,
          };
          each(keys(transformedValues), (key: string) => {
            setValue(key, get(transformedValues, key));
          });
          triggerSubmit();
        } catch (err) {
          console.log(err);
        }
      }
      const otherData = getItem(OTHER_DATA_KEY);
      if (otherData) {
        // Parse it to a javaScript object
        try {
          const parsedOtherData = JSON.parse(otherData);
          setSearchFieldOpened(get(parsedOtherData, 'expanded', false));
        } catch (err) {
          console.log(err);
        }
      }
    } else {
      removeItem(FORM_DATA_KEY);
      removeItem(OTHER_DATA_KEY);
    }
    toggleLoader(false);
  };

  useEffect(() => {
    loadEntities();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const callPersistForm = (value: any) => {
    putItem(FORM_DATA_KEY, JSON.stringify(value));
  };

  const putToStorage = (expandedValue: boolean) => {
    putItem(
      OTHER_DATA_KEY,
      JSON.stringify({
        expanded: expandedValue,
      }),
    );
  };

  useEffect(() => {
    if (!isEmpty(fields) && isArray(fields)) {
      const firstItemName = get(fields[0], 'name');
      setFocus(firstItemName);
    }
  }, [fields, setFocus]);

  return (
    <>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(submitHandler)}>
          <button hidden={true} ref={refSubmitButtom} type={'submit'} />
          <Box sx={{ position: 'relative', boxShadow: searchFieldOpened ? 4 : 'none' }}>
            <Box sx={{ position: 'relative', zIndex: 1300 }}>
              <Papeer bottomMargin={true}>
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: {
                      xs: 'wrap',
                      lg: 'nowrap',
                    },
                    paddingRight: '48px',
                    position: 'relative',
                  }}
                >
                  <Box sx={{ position: 'absolute', right: 0, top: 8 }}>
                    <IconButton
                      aria-label="delete"
                      onClick={() => {
                        setSearchFieldOpened(!searchFieldOpened);
                        putToStorage(!searchFieldOpened);
                      }}
                    >
                      {searchFieldOpened ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                    </IconButton>
                  </Box>

                  {filter(fields, { searchType: 'basic' }).map((field) => (
                    <Box sx={mainSx} key={field.name}>
                      {field.type === 'select' && (
                        <FormSelect
                          name={get(field, 'name')}
                          label={get(field, 'label')}
                          items={get(field, 'items')}
                          errors={errors}
                          sx={{ borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }}
                        />
                      )}
                      {get(field, 'type') === 'datePicker' && (
                        <FormDatePicker
                          name={field.name}
                          label={field.label}
                          errors={errors}
                          sx={{ borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }}
                        />
                      )}
                      {!get(field, 'type') && (
                        <FormInput
                          {...field}
                          errors={errors}
                          InputProps={{
                            sx: { borderTopRightRadius: 0, borderBottomRightRadius: 0 },
                          }}
                        />
                      )}
                    </Box>
                  ))}
                </Box>
                <Grid2 container={true} justifyContent="flex-end">
                  <Grid2 size={{ xs: 12, md: 4, lg: 3 }}>
                    <Box sx={{ mt: 1 }} display="flex" gap={1} justifyContent="flex-end">
                      <Button
                        variant="contained"
                        color="inherit"
                        type="button"
                        disabled={!canSearch}
                        onClick={() => resetFields()}
                      >
                        {t('reset')}
                      </Button>
                      <Button
                        variant="contained"
                        color="primary"
                        type="submit"
                        disabled={!canSearch}
                        className="search-form-button"
                        data-tour="searchButtonTours"
                      >
                        {t('search')}
                      </Button>
                    </Box>
                  </Grid2>
                </Grid2>
              </Papeer>
            </Box>
            <Box sx={searchFieldOpened ? undefined : { display: 'none' }}>
              <Box
                sx={{
                  position: 'absolute',
                  background: (theme) => theme.palette.background.paper,
                  zIndex: 1030,
                  p: 2,
                  pt: 0,
                  left: 0,
                  right: 0,
                  top: '100%',
                  boxShadow: 4,
                  borderBottomLeftRadius: (theme) => theme.shape.borderRadius,
                  borderBottomRightRadius: (theme) => theme.shape.borderRadius,
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: {
                      xs: 'wrap',
                    },
                  }}
                >
                  {filter(fields, { searchType: 'extended' }).map((field) => (
                    <Box
                      key={field.name}
                      sx={{
                        width: { xs: '100%', md: '50%', lg: 1 / 5, xl: 1 / 7 },
                        pr: { lg: 1 },
                      }}
                    >
                      <Grid2 size={{ xs: 12, md: 4, lg: 3 }}>
                        {field.type === 'select' && (
                          <FormSelect
                            name={get(field, 'name')}
                            label={get(field, 'label')}
                            items={get(field, 'items')}
                            errors={errors}
                          />
                        )}
                        {get(field, 'type') === 'datePicker' && (
                          <FormDatePicker name={field.name} label={field.label} errors={errors} />
                        )}
                        {!get(field, 'type') && <FormInput {...field} errors={errors} />}
                      </Grid2>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Box>
          </Box>
        </form>
      </FormProvider>
    </>
  );
};

export default PatientSearchForm;
